import React, { useState, useEffect } from 'react';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CircularProgress from '@mui/material/CircularProgress';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { apiClient } from '../services/apiClient';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  IconButton,
  Alert,
  Snackbar,
  Card,
  CardContent,
  CardHeader
} from '@mui/material';

const tableStyles = {
  tableCell: {
    padding: '8px 16px',
    height: '50px',
    whiteSpace: 'nowrap',
  },
  headerCell: {
    padding: '8px 16px',
    height: '50px',
    backgroundColor: '#f5f5f5',
    fontWeight: 'bold',
  }
};

export default function Subscriptions() {
  const [subscriptions, setSubscriptions] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openDialog, setOpenDialog] = useState(false);
  const [openAlertDialog, setOpenAlertDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [openDeleteAlertDialog, setOpenDeleteAlertDialog] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [subscriptionForm, setSubscriptionForm] = useState({
    id: '',
    subscription_limit: ''
  });
  const [alertForm, setAlertForm] = useState({
    id: '',
    percentage: '',
    template: ''
  });
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Quill modules configuration
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, false] }],
      ['bold', 'italic', 'underline'],
      [{ 'color': [] }, { 'background': [] }],
      [{'list': 'ordered'}, {'list': 'bullet'}],
      ['link'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline',
    'color', 'background',
    'list', 'bullet',
    'link'
  ];

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [subsData, alertsData] = await Promise.all([
        apiClient.get('/api/admin/subscriptions'),
        apiClient.get('/api/admin/subscriptions/alerts')
      ]);

      setSubscriptions(subsData);
      setAlerts(alertsData);
      setError(null);
    } catch (error) {
      console.error('Error fetching data:', error);
      setError('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const handleOpenDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setSubscriptionForm({
        id: item.id,
        subscription_limit: item.subscription_limit
      });
    } else {
      setSelectedItem(null);
      setSubscriptionForm({
        id: '',
        subscription_limit: ''
      });
    }
    setOpenDialog(true);
  };

  const handleOpenAlertDialog = (item = null) => {
    if (item) {
      setSelectedItem(item);
      setAlertForm({
        id: item.id,
        percentage: item.percentage,
        template: item.template
      });
    } else {
      setSelectedItem(null);
      setAlertForm({
        id: '',
        percentage: '',
        template: ''
      });
    }
    setOpenAlertDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedItem(null);
    setSubscriptionForm({
      id: '',
      subscription_limit: ''
    });
  };

  const handleCloseAlertDialog = () => {
    setOpenAlertDialog(false);
    setSelectedItem(null);
    setAlertForm({
      id: '',
      percentage: '',
      template: ''
    });
  };

  const handleOpenDeleteDialog = (item) => {
    setSelectedItem(item);
    setOpenDeleteDialog(true);
  };

  const handleOpenDeleteAlertDialog = (item) => {
    setSelectedItem(item);
    setOpenDeleteAlertDialog(true);
  };

  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setSelectedItem(null);
  };

  const handleCloseDeleteAlertDialog = () => {
    setOpenDeleteAlertDialog(false);
    setSelectedItem(null);
  };

  const handleSubscriptionSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (selectedItem) {
        await apiClient.put(`/api/admin/subscriptions/${selectedItem.id}`, subscriptionForm);
      } else {
        await apiClient.post('/api/admin/subscriptions', subscriptionForm);
      }
      await fetchData();
      handleCloseDialog();
      showSnackbar(selectedItem ? 'Abonnement mis à jour avec succès' : 'Abonnement créé avec succès');
    } catch (error) {
      console.error('Error saving subscription:', error);
      showSnackbar('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAlertSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      if (selectedItem) {
        await apiClient.put(`/api/admin/subscriptions/alerts/${selectedItem.id}`, alertForm);
      } else {
        await apiClient.post('/api/admin/subscriptions/alerts', alertForm);
      }
      await fetchData();
      handleCloseAlertDialog();
      showSnackbar(selectedItem ? 'Alerte mise à jour avec succès' : 'Alerte créée avec succès');
    } catch (error) {
      console.error('Error saving alert:', error);
      showSnackbar('Erreur lors de l\'enregistrement', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      setSaving(true);
      await apiClient.delete(`/api/admin/subscriptions/${selectedItem.id}`);
      await fetchData();
      handleCloseDeleteDialog();
      showSnackbar('Abonnement supprimé avec succès');
    } catch (error) {
      console.error('Error deleting subscription:', error);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteAlert = async () => {
    try {
      setSaving(true);
      await apiClient.delete(`/api/admin/subscriptions/alerts/${selectedItem.id}`);
      await fetchData();
      handleCloseDeleteAlertDialog();
      showSnackbar('Alerte supprimée avec succès');
    } catch (error) {
      console.error('Error deleting alert:', error);
      showSnackbar('Erreur lors de la suppression', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleAlertFormChange = (e) => {
    const { name, value } = e.target;
    setAlertForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="p-8 text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 rounded-lg">
      {/* Subscriptions Section */}
      <div className="px-6 py-4">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Gestion des abonnements
          </h2>
          <button
            onClick={() => handleOpenDialog()}
            className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
          >
            <AddIcon className="-ml-1 mr-2 h-5 w-5" />
            Nouvel abonnement
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Limite d'abonnement
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {subscriptions.map((subscription) => (
                <tr key={subscription.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {subscription.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {subscription.subscription_limit}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenDialog(subscription)}
                      className="text-blue-600 hover:text-blue-900 mr-2"
                    >
                      <EditIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteDialog(subscription)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <DeleteIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {subscriptions.length === 0 && (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucun abonnement trouvé
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Alerts Section */}
      <div className="px-6 py-4 border-t border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-medium text-gray-900">
            Gestion des alertes
          </h2>
          <button
            onClick={() => handleOpenAlertDialog()}
            className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
          >
            <AddIcon className="-ml-1 mr-2 h-5 w-5" />
            Nouvelle alerte
          </button>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Pourcentage
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Template
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {alerts.map((alert) => (
                <tr key={alert.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {alert.id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {alert.percentage}%
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div 
                      className="max-w-xl line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: alert.template }}
                    />
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleOpenAlertDialog(alert)}
                      className="text-blue-600 hover:text-blue-900 mr-2"
                    >
                      <EditIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleOpenDeleteAlertDialog(alert)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <DeleteIcon className="h-5 w-5" />
                    </button>
                  </td>
                </tr>
              ))}
              {alerts.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-4 text-center text-sm text-gray-500">
                    Aucune alerte trouvée
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Create/Edit Subscription Dialog */}
      {openDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedItem ? 'Modifier l\'abonnement' : 'Créer un abonnement'}
              </h3>
            </div>

            <form onSubmit={handleSubscriptionSubmit}>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div>
                    <label htmlFor="id" className="block text-sm font-medium text-gray-700 mb-1">
                      ID
                    </label>
                    <input
                      type="text"
                      id="id"
                      name="id"
                      value={subscriptionForm.id}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, id: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                      disabled={!!selectedItem}
                    />
                  </div>

                  <div>
                    <label htmlFor="subscription_limit" className="block text-sm font-medium text-gray-700 mb-1">
                      Limite d'abonnement
                    </label>
                    <input
                      type="number"
                      id="subscription_limit"
                      name="subscription_limit"
                      value={subscriptionForm.subscription_limit}
                      onChange={(e) => setSubscriptionForm({ ...subscriptionForm, subscription_limit: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
                <button
                  type="button"
                  onClick={handleCloseDialog}
                  className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className={`inline-flex justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 
                    ${!saving ? 'hover:bg-[rgb(121,192,189)]' : 'opacity-75 cursor-not-allowed'}
                    focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2`}
                >
                  {saving ? 'Enregistrement...' : selectedItem ? 'Enregistrer' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Create/Edit Alert Dialog */}
      {openAlertDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg w-full max-w-2xl">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                {selectedItem ? 'Modifier l\'alerte' : 'Créer une alerte'}
              </h3>
            </div>

            <form onSubmit={handleAlertSubmit}>
              <div className="px-6 py-4">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label htmlFor="alert_id" className="block text-sm font-medium text-gray-700 mb-1">
                        ID
                      </label>
                      <input
                        type="text"
                        id="alert_id"
                        name="id"
                        value={alertForm.id}
                        onChange={handleAlertFormChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)] sm:text-sm"
                        required
                        disabled={!!selectedItem}
                      />
                    </div>
                    <div>
                      <label htmlFor="percentage" className="block text-sm font-medium text-gray-700 mb-1">
                        Pourcentage
                      </label>
                      <input
                        type="number"
                        id="percentage"
                        name="percentage"
                        value={alertForm.percentage}
                        onChange={handleAlertFormChange}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[rgb(141,212,209)] focus:ring-[rgb(141,212,209)] sm:text-sm"
                        required
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
                      Template
                    </label>
                    <div className="mt-1">
                      <ReactQuill
                        theme="snow"
                        value={alertForm.template}
                        onChange={(content) => setAlertForm(prev => ({ ...prev, template: content }))}
                        modules={modules}
                        formats={formats}
                        className="bg-white"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setOpenAlertDialog(false)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[rgb(141,212,209)]"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="inline-flex items-center justify-center rounded-md bg-[rgb(141,212,209)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(121,192,189)] focus:outline-none focus:ring-2 focus:ring-[rgb(141,212,209)] focus:ring-offset-2"
                >
                  {saving ? (
                    <>
                      <CircularProgress size={20} className="mr-2" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Subscription Dialog */}
      {openDeleteDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer cet abonnement ?
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseDeleteDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                onClick={handleDelete}
                disabled={saving}
                className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {saving ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Alert Dialog */}
      {openDeleteAlertDialog && (
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center p-4 z-50 ml-[240px]">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Confirmer la suppression
              </h3>
            </div>

            <div className="px-6 py-4">
              <p className="text-gray-700">
                Êtes-vous sûr de vouloir supprimer cette alerte ?
              </p>
            </div>

            <div className="px-6 py-4 bg-gray-50 flex justify-end space-x-3 rounded-b-lg">
              <button
                type="button"
                onClick={handleCloseDeleteAlertDialog}
                className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteAlert}
                disabled={saving}
                className="inline-flex justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {saving ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
} 